import { CSSProperties, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TypeModal } from "../entities/RoleTypeEditor";
import { ModalShell } from "../../../atoms/ModalShell";
import { FaChevronDown, FaLock } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { TypeEditorType, UserType } from "../../../../types/UserRolesTypes";
import { useUpdateType } from "../../../../hooks/users";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

export const TypeOptionsPart = ({
  data,
  customerId,
  entity_identification,
  user_identification,
  list_type,
  disabled,
}: TypeEditorType) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const canModifyType = permissions.can(Cap.USERS_USER_TYPE, Level.WRITE);
  const [types, setTypes] = useState<Array<UserType>>(list_type);
  const [type, setType] = useState<string>(data.type);
  const [showModalType, setShowModalType] = useState(false);

  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { mutate: updateType } = useUpdateType();

  const protectedTypes = ["ADMIN"];

  const prevCustomerIdRef = useRef<string>(customerId);

  useEffect(() => {
    setTypes(list_type);
  }, [list_type]);

  useEffect(() => {
    if (prevCustomerIdRef.current !== customerId) {
      setType(data.type);
      prevCustomerIdRef.current = customerId;
    }
  }, [data, customerId]);

  useEffect(() => {
    if (!open) return;
    const positionMenu = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        setOpen(false);
        return;
      }
      const gap = 4;
      const estimatedHeight = 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
      setMenuStyle({
        position: "fixed",
        left: rect.left,
        minWidth: rect.width,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap }),
      });
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    positionMenu();
    window.addEventListener("scroll", positionMenu, true);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", positionMenu, true);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleTypeManagement = (e: string) => {
    const optionValue = e;
    setOpen(false);

    if (optionValue !== "addType") {
      updateType(
        {
          user_identification: user_identification,
          entity_identification: entity_identification,
          customerId: customerId,
          type: optionValue,
        },
        {
          onSuccess: () => setType(optionValue),
        }
      );
    } else {
      setShowModalType(true);
    }
  };

  return (
    <div className="role-option-part-drop-down">
      <button
        type="button"
        ref={triggerRef}
        translate="no"
        disabled={!canModifyType}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => canModifyType && setOpen((o) => !o)}
        className={`group w-full justify-between items-center flex gap-1.5 ${
          !canModifyType ? "cursor-not-allowed" : ""
        }`}
      >
        <div className="text-gray-500 text-sm font-normal font-figtree leading-tight text-left md:text-nowrap flex items-center gap-1.5">
          {protectedTypes.includes(type) && (
            <FaLock
              size={14}
              className="text-amber-500 shrink-0"
              title="Protected type"
            />
          )}
          {type}
        </div>
        {canModifyType && (
          <div className="w-5 h-5 relative ml-1 group-hover:opacity-100 opacity-0">
            <FaChevronDown size={14} />
          </div>
        )}
      </button>

      {open &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setOpen(false)}
            />
            <div
              role="menu"
              translate="no"
              style={menuStyle}
              className="z-[9999] w-fit rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none"
            >
              <div className="max-h-[200px] overflow-y-auto">
                {types.map((typeItem: UserType, index: number) => (
                  <button
                    type="button"
                    role="menuitem"
                    key={index}
                    onClick={() => handleTypeManagement(typeItem.user_type)}
                    className="flex w-full items-center gap-1 whitespace-nowrap px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                  >
                    {protectedTypes.includes(typeItem.user_type) && (
                      <FaLock
                        size={14}
                        className="text-amber-500 shrink-0"
                        title="Protected type"
                      />
                    )}
                    <span>{typeItem.user_type}</span>
                  </button>
                ))}
              </div>
              <div className="my-1 h-px bg-gray-100" />
              <button
                type="button"
                role="menuitem"
                translate="no"
                onClick={() => handleTypeManagement("addType")}
                className="flex w-full items-center whitespace-nowrap px-4 py-2 text-left text-blue-500 hover:bg-gray-100"
              >
                {t("dashboard.RoleTypeEditor.btn-03")}
              </button>
            </div>
          </>,
          document.body
        )}

      <ModalShell
        size="lg"
        show={showModalType}
        onClose={() => setShowModalType(false)}
        title={t("dashboard.RoleTypeEditor.modal.header-02")}
        subtitle={t("dashboard.RoleTypeEditor.modal.description-02")}
      >
        <TypeModal
          types={types}
          setTypes={setTypes}
          setShowTypeModal={setShowModalType}
          setType={setType}
          user_identification={user_identification}
          entity_identification={entity_identification}
          customerId={customerId}
          disabled={disabled}
        />
      </ModalShell>
    </div>
  );
};
